import React, { useEffect, useState } from 'react'
import { Select, Label, Modal, Button, Row, Column } from '../../../components'
import PolicyMappingDataService from '../PolicyMappingDataService'
import Logger from '../../../shared/services/Logger'
import * as label from '../../../shared/util/label';
import { LookupDetailConstants } from '../../../shared/util/LookupDetailConstants';
import { ApplyPolicy } from '../../../shared/util/constant';
import { SelectOption } from '../../../shared/components/input';
import { CustomerPolicyDetailLabels as cstPolicyDetailLabel } from '../../../shared/util/label';
import * as constant from '../../../shared/util/constant';

interface Props {
    showApplyPolicyPopup: boolean
    handleClose: () => void
    agencyList: any[]
    sourcesTypeList: any[]
    firstName: string
    lastName: string
    middleName: string
    agencyId: string
    parentCompany?: string
    writtingCompany?: string
    showCompanyInfo ?: boolean
    onSave?: (selectedAgency: any, selectedSourceType: any, selectedSource: any,  selectedParentCompany: any, selectedWrittingCompany: any) => void;
}


const ApplyPolicyPopup = (props: Props) => {

    const { showApplyPolicyPopup, agencyList, sourcesTypeList, firstName, lastName, middleName, agencyId,handleClose, onSave, parentCompany, writtingCompany, showCompanyInfo } = props;
    const [selectedAgency, setSelectedAgency] = useState<any>(agencyId);
    const [selectedSourceType, setSelectedSourceType] = useState<any>(null);
    const [selectedSource, setSelectedSource] = useState<any>(null);
    const [sourcesList, setSourcesList] = useState<any[]>([]); // Local state for sourcesList
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [validation, setValidation] = useState<any>({});
    const policyMappingDataService = new PolicyMappingDataService();
    
    const [insuranceCompanyList, setInsuranceCompanyList] = useState<SelectOption[]>([]);
    const [writingCompanyList, setWritingCompanyList] = useState<any[]>([]);
    const [filteredWritingCompanies, setFilteredWritingCompanies] = useState<SelectOption[]>([]);
    const [selectedParentCompany, setSelectedParentCompany] = useState<any>(null);
    const [selectedWrittingCompany, setSelectedWrittingCompany] = useState<any>(null);

    useEffect(() => {
        initializeComponent();
    }, []);

    useEffect(() => {
        if(showCompanyInfo)
        {
        setSelectedParentCompany(parentCompany);
        setSelectedWrittingCompany(writtingCompany);
        }
    }, [parentCompany, writtingCompany, showCompanyInfo]);

    // Set initial filtered writing companies based on selected parent company
    useEffect(() => {
        if (selectedParentCompany && writingCompanyList.length > 0) {
            const filteredCompanies = writingCompanyList
                .filter(company => company.parentId === selectedParentCompany && company.parentId !== company.value);
            setFilteredWritingCompanies(filteredCompanies);
        }
    }, [selectedParentCompany, writingCompanyList]);

    const initializeComponent = async () => {
        const { companyListLookup, writingCompaniesLookup } = await policyMappingDataService.fetchCompanyData();
        setInsuranceCompanyList(companyListLookup);
        setWritingCompanyList(writingCompaniesLookup);
    }


    // Validation logic for each field
    const validateFields = () => {
        const errors: any = {};
        if (!selectedAgency) errors.selectedAgency = 'Please select an agency.';
        if (!selectedSourceType) errors.selectedSourceType = 'Please select a source type.';
        if (!selectedSource) errors.selectedSource = 'Please select a source.';
        if (showCompanyInfo) {
            if (!selectedParentCompany) errors.selectedParentCompany = 'Please select a parent company.';
            if (!selectedWrittingCompany) errors.selectedWrittingCompany = 'Please select a writing company.';
        }
        return errors;
    };

    const handleSave = () => {
        setFormSubmitted(true);
        const errors = validateFields();
        setValidation(errors);
        const hasCompanyInfo = showCompanyInfo ? (selectedParentCompany && selectedWrittingCompany) : true;
        if (Object.keys(errors).length === 0 && Boolean(firstName && lastName && selectedAgency && selectedSource && selectedSourceType && hasCompanyInfo)) {
            if (onSave) onSave(selectedAgency, selectedSourceType, selectedSource, selectedParentCompany, selectedWrittingCompany);
        }
    };

    const handleAgencyChange = (value: string) => {
        setSelectedAgency(value);
        setValidation((prev: any) => {
            const { selectedAgency, ...rest } = prev;
            return rest;
        });
    };

    const handleSourceChange = (value: string) => {
        setSelectedSource(value);
        setValidation((prev: any) => {
            const { selectedSource, ...rest } = prev;
            return rest;
        });
    };
    const handleSourceTypeChange = (value: string) => {
        fetchSourceTypeLookupValue(value);
        setSelectedSourceType(value);
        setValidation((prev: any) => {
            const { selectedSourceType, ...rest } = prev;
            return rest;
        });
    };

    const handleParentCompanyChange = (value: string | null) => {
        if (!value) {
            // If parent company is deselected, do not show any writing companies
            setFilteredWritingCompanies([]);
        } else {
            // Filter writing companies based on selected parent company
            const filteredCompanies = writingCompanyList
                .filter(company => company.parentId === value && company.parentId !== company.value);
            setFilteredWritingCompanies(filteredCompanies.length > 0 ? filteredCompanies : []);
        }

        // Update parent company and reset writing company
        setSelectedParentCompany(value || null);
        setSelectedWrittingCompany(null);
        setValidation((prev: any) => {
            const { selectedParentCompany, ...rest } = prev;
            return rest;
        });
    };

    const handleWrittingCompanyChange = (value: string | null) => {
        if (!value) {
            setSelectedWrittingCompany(null);
            // If parent company is selected, show filtered list, otherwise show none
            if (selectedParentCompany) {
                const filteredCompanies = writingCompanyList
                    .filter(company => company.parentId === selectedParentCompany && company.parentId !== company.value);
                setFilteredWritingCompanies(filteredCompanies.length > 0 ? filteredCompanies : []);
            } else {
                setFilteredWritingCompanies([]);
            }
        } else {
            // Find the selected writing company
            const selectedCompany = writingCompanyList.find(company => company.value === value && company.value !== company.parentId);
            if (selectedCompany) {
                setSelectedWrittingCompany(value);
            }
        }
        setValidation((prev: any) => {
            const { selectedWrittingCompany, ...rest } = prev;
            return rest;
        });
    };

    const fetchSourceTypeLookupValue = async (sourceTypeId: string) => {
        try {
            const sourceTypeLookupValue = await policyMappingDataService.getSourceTypeLookupValue(sourceTypeId);
            if (sourceTypeLookupValue) {
                const sourceList = await policyMappingDataService.getSourceList(sourceTypeLookupValue.lookupCode);
                setSourcesList(sourceList || []); // Set local state
                setSelectedSource(null); // Reset selected source when type changes
            } else {
                Logger.logError("No source type found for ID:", sourceTypeId);
                setSourcesList([]);
                setSelectedSource(null);
            }
        } catch (error) {
            Logger.logError("Error fetching source type lookup value:", error);
            setSourcesList([]);
            setSelectedSource(null);
        }
    };


    // Validation method for all field checks
    const getValidationError = () => {
        const hasCompanyInfo = showCompanyInfo ? (selectedParentCompany && selectedWrittingCompany) : true;
        if (!firstName || !lastName || !selectedAgency || !selectedSourceType || !selectedSource || !hasCompanyInfo) {
            return "Mandatory fields missing. Please manually create the customer and policy.";
        }
        return null;
    };

    useEffect(() => {
        if (showApplyPolicyPopup) {
            //setSelectedAgency(null);
            //setSelectedSourceType(null);
            setSourcesList([]); 
            setSelectedSource(null);
            setFormSubmitted(false);
            setValidation({});
            setSelectedSourceType(LookupDetailConstants.DEFAULT_VALUE.Builder_Source_Type); // Default source type
            fetchSourceTypeLookupValue(LookupDetailConstants.DEFAULT_VALUE.Builder_Source_Type); // Fetch sources for default source type
            setSelectedAgency(agencyId || null); // Set agencyId if provided
            if(showCompanyInfo)
            {
                setSelectedParentCompany(parentCompany || null);
                setSelectedWrittingCompany(writtingCompany || null);
            }
        }
    }, [showApplyPolicyPopup]);

    return (

        <Modal
            showHeaderCloseButton={false}
            backdrop="static"
            className="create-customer-apply-policy-modal"
            verticallyCentered={true}
            title={ApplyPolicy.TitleApplyTransaction}
            show={showApplyPolicyPopup}
            toggle={() => { }} // Disable closing on backdrop or header click
            body={
                <>
                    <Row className="mb-3">
                        <Label text='Basic Customer Information' className={'mt-3'} />
                        <Column lg={4} md={4} sm={12}>
                            <input type="text" value={firstName} disabled className="form-control" />
                        </Column>
                        <Column lg={4} md={4} sm={12}>
                            <input type="text" value={middleName} disabled className="form-control" />
                        </Column>
                        <Column lg={4} md={4} sm={12}>
                            <input type="text" value={lastName} disabled className="form-control" />
                        </Column>
                    </Row>
                    <Row className="mb-3">
                        <Label text='Generic' className={'mt-3'} />
                        <Column lg={4} md={4} sm={12}>
                            <Select
                                id="ddAgencyList"
                                controlId="ddAgencyList"
                                className='mb-3'
                                label={<>{"Agency"}<span className="text-danger ms-1">*</span></>}
                                options={agencyList}
                                onChange={(values) => handleAgencyChange(values[0] as string)}
                                defaultSelected={agencyList.filter(option =>
                                    option.value === selectedAgency
                                )}
                                disabled={agencyId ? true : false}
                                isInvalid={!!validation.selectedAgency}
                                invalidWarning={validation.selectedAgency}
                            />
                        </Column>
                        <Column lg={4} md={4} sm={12}>
                            <Select
                                id="ddSourceTypeList"
                                controlId="ddSourceTypeList"
                                className='mb-3'
                                label={<>{"Source Type"}<span className="text-danger ms-1">*</span></>}
                                options={sourcesTypeList}
                                onChange={(values) => handleSourceTypeChange(values[0] as string)}
                                defaultSelected={sourcesTypeList?.filter(option =>
                                    option.value === selectedSourceType
                                )}
                                isInvalid={!!validation.selectedSourceType}
                                invalidWarning={validation.selectedSourceType}
                            />
                        </Column>
                        <Column lg={4} md={4} sm={12}>
                            <Select
                                id="ddSourceList"
                                controlId="ddSourceList"
                                className='mb-3'
                                label={<>{"Source"}<span className="text-danger ms-1">*</span></>}
                                options={sourcesList}
                                onChange={(values) => handleSourceChange(values[0] as string)}
                                defaultSelected={sourcesList?.filter(option =>
                                    option.value === selectedSource
                                )}
                                isInvalid={!!validation.selectedSource}
                                invalidWarning={validation.selectedSource}
                            />
                        </Column>
                    </Row>
                    
                    {showCompanyInfo && (
                        <Row className="mb-3">
                            <Column lg={4} md={4} sm={12}>
                                <Select
                                    id='insuranceCompany'
                                    label={<>{cstPolicyDetailLabel.PARENT_COMPANY}<span className="text-danger ms-1">*</span></>}
                                    options={insuranceCompanyList}
                                    defaultSelected={insuranceCompanyList?.filter(({ value }) =>
                                        value === selectedParentCompany
                                    )}
                                    onChange={(values) => {
                                        handleParentCompanyChange(values[0] as string)
                                    }}
                                    placeholder={constant.PLEASE_SELECT}
                                    className='mb-3'
                                    isInvalid={!!validation.selectedParentCompany}
                                    invalidWarning={validation.selectedParentCompany}
                                />
                            </Column>
                            <Column lg={4} md={4} sm={12}>
                                <Select
                                    id='writingCompanyId'
                                    label={<>{cstPolicyDetailLabel.WRITING_COMPANY}<span className="text-danger ms-1">*</span></>}
                                    options={filteredWritingCompanies}
                                    defaultSelected={filteredWritingCompanies?.filter(({ value }) =>
                                        value === selectedWrittingCompany
                                    )}
                                    onChange={(values) => {
                                        handleWrittingCompanyChange(values[0] as string)
                                    }}
                                    placeholder={constant.PLEASE_SELECT}
                                    multiple={false}
                                    showAsFloatingLabel={true}
                                    className='mb-3'
                                    isInvalid={!!validation.selectedWrittingCompany}
                                    invalidWarning={validation.selectedWrittingCompany}
                                />
                            </Column>
                        </Row>
                    )}
                </>
            }
            closeButton={
                {
                    size: "small",
                    icon: 'cancel',
                    outlined: true,
                    color: 'link',
                    className: "create-customer-apply-policy-modal-cancel-btn",
                    onClick: () => handleClose(),
                    children: <span className=".create-customer-apply-policy-modal-cancel-btn-cancel-btn-label">CANCEL</span>

                }
            }
            successButton={
                {
                    size: "small",
                    icon: "refresh",
                    color: 'link',
                    className: "create-customer-apply-policy-modal-success-btn",
                    onClick: handleSave,
                    children: <span className="">{label.UnmatchedPolicy.APPLY}</span>
                }
            }
            buttonsAlignment={"edges"}
        />

    )
}
export default ApplyPolicyPopup
